/**
 * The Recorded wall's timeline: one lane per camera on the wall, one shared axis, one playhead
 * through all of them.
 *
 * Seeking is for the WALL, not a camera — a click or a drag anywhere on the lanes or the axis
 * moves the one clock, and every tile follows. Dragging publishes a throttled stream of seeks so
 * the tiles track the finger, then a final one on release.
 *
 * Zoom: wheel over the lanes (around the cursor), or the − / + / fit buttons. Pan: shift+wheel,
 * a horizontal wheel, or drag the window on the overview bar underneath. While playing, the view
 * follows the playhead once it leaves the visible window.
 *
 * Presentational: lanes, range and time in; seeks out. The left column's top cell is a slot for
 * the host's transport and date controls, so the labels below it line up with their lanes.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import type { RecordingSegment } from '../../api/contracts';
import { TRIGGER_COLOURS, TRIGGER_LABELS } from '../recordings/RecordingTimeline';
import type { TileStatus } from './PlaybackWallTile';

export interface TimelineLane {
  key: string;
  label: string;
  /** The camera's accent — the dot its tile shows too. */
  color: string;
  /** undefined = still fetching. */
  segments: RecordingSegment[] | undefined;
  status?: TileStatus;
}

export interface MultiCameraTimelineProps {
  lanes: TimelineLane[];
  rangeStartMs: number;
  rangeEndMs: number;
  timeMs: number;
  /** `final` is false for the intermediate seeks of a drag. */
  onSeek: (ms: number, final: boolean) => void;
  /** The view follows the playhead — true while playing. */
  follow?: boolean;
  /** Left column, top cell: transport, readout, date + range. */
  header?: ReactNode;
  /** Axis title — the day being shown. */
  dateLabel?: string;
  className?: string;
}

const LABEL_W = 'w-60 xl:w-64';
const HEAD_H = 100;
const LANE_H = 26;
const MIN_SPAN_MS = 2 * 60 * 1000;
const DRAG_SEEK_MS = 300;

const TICK_STEPS_SEC = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600];
const TARGET_TICKS = 9;

const STATUS_TEXT: Partial<Record<TileStatus, string>> = {
  segments: 'loading…',
  loading: 'loading…',
  buffering: 'buffering…',
  gap: 'no footage here',
  'no-footage': 'no recordings',
  error: 'error',
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function fmtTick(ms: number, withSeconds: boolean) {
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}), hour12: false,
  });
}
function fmtSpan(ms: number) {
  const mins = ms / 60000;
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Adjacent segments with the same trigger drawn as one block — 288 five-minute divs a day otherwise. */
function blocksFor(segments: RecordingSegment[], vs: number, ve: number) {
  const out: { start: number; end: number; trigger: string }[] = [];
  const sorted = [...segments]
    .map((s) => ({
      start: new Date(s.start_time).getTime(),
      end: s.status === 'in-progress' ? Math.max(new Date(s.end_time).getTime(), Date.now()) : new Date(s.end_time).getTime(),
      trigger: s.trigger || 'continuous',
    }))
    .filter((b) => b.end > vs && b.start < ve)
    .sort((a, b) => a.start - b.start);
  for (const b of sorted) {
    const last = out[out.length - 1];
    if (last && last.trigger === b.trigger && b.start - last.end < 2000) last.end = Math.max(last.end, b.end);
    else out.push({ ...b });
  }
  return out;
}

export default function MultiCameraTimeline({
  lanes,
  rangeStartMs,
  rangeEndMs,
  timeMs,
  onSeek,
  follow = false,
  header,
  dateLabel,
  className = '',
}: MultiCameraTimelineProps) {
  const fullSpan = Math.max(MIN_SPAN_MS, rangeEndMs - rangeStartMs);
  const [view, setView] = useState({ start: rangeStartMs, end: rangeStartMs + fullSpan });
  // A new range is a new picture: show all of it.
  useEffect(() => { setView({ start: rangeStartMs, end: rangeStartMs + fullSpan }); }, [rangeStartMs, fullSpan]);

  const span = view.end - view.start;
  const setViewClamped = useCallback((start: number, s: number) => {
    const sp = clamp(s, MIN_SPAN_MS, fullSpan);
    const st = clamp(start, rangeStartMs, rangeStartMs + fullSpan - sp);
    setView({ start: st, end: st + sp });
  }, [rangeStartMs, fullSpan]);

  // ─── Follow the playhead while playing ───
  const draggingRef = useRef(false);
  useEffect(() => {
    if (!follow || draggingRef.current) return;
    if (timeMs < view.start || timeMs > view.end) setViewClamped(timeMs - span * 0.1, span);
  }, [follow, timeMs, view.start, view.end, span, setViewClamped]);

  // ─── Geometry ───
  const trackRef = useRef<HTMLDivElement>(null);
  const msAt = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return view.start;
    const r = el.getBoundingClientRect();
    return view.start + clamp((clientX - r.left) / Math.max(1, r.width), 0, 1) * span;
  }, [view.start, span]);
  const pct = (ms: number) => ((ms - view.start) / span) * 100;

  // ─── Scrub ───
  const lastDragSeekRef = useRef(0);
  const [hover, setHover] = useState<number | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    draggingRef.current = true;
    lastDragSeekRef.current = performance.now();
    onSeek(msAt(e.clientX), false);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const ms = msAt(e.clientX);
    setHover(ms);
    if (!draggingRef.current) return;
    const now = performance.now();
    if (now - lastDragSeekRef.current >= DRAG_SEEK_MS) {
      lastDragSeekRef.current = now;
      onSeek(ms, false);
    }
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onSeek(msAt(e.clientX), true);
  };

  // ─── Wheel zoom / pan — non-passive, or the page scrolls instead ───
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      const sp = v.end - v.start;
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const d = (e.shiftKey ? e.deltaY : e.deltaX) || 0;
        setViewClamped(v.start + (d / 400) * sp, sp);
        return;
      }
      const r = el.getBoundingClientRect();
      const f = clamp((e.clientX - r.left) / Math.max(1, r.width), 0, 1);
      const anchor = v.start + f * sp;
      const next = sp * (e.deltaY > 0 ? 1.25 : 0.8);
      const ns = clamp(next, MIN_SPAN_MS, fullSpan);
      setViewClamped(anchor - f * ns, ns);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setViewClamped, fullSpan]);

  const zoomBy = (factor: number) => {
    const anchor = timeMs >= view.start && timeMs <= view.end ? timeMs : view.start + span / 2;
    const f = (anchor - view.start) / span;
    const ns = clamp(span * factor, MIN_SPAN_MS, fullSpan);
    setViewClamped(anchor - f * ns, ns);
  };

  // ─── Ticks ───
  const ticks = useMemo(() => {
    const stepSec = TICK_STEPS_SEC.find((s) => (span / 1000) / s <= TARGET_TICKS) ?? 21600;
    const step = stepSec * 1000;
    const d = new Date(view.start);
    const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const first = midnight + Math.ceil((view.start - midnight) / step) * step;
    const out: { ms: number; label: string }[] = [];
    for (let t = first; t <= view.end; t += step) out.push({ ms: t, label: fmtTick(t, stepSec < 60) });
    // Minor ticks: 4 between majors.
    const minor: number[] = [];
    for (let t = first - step; t <= view.end; t += step / 5) if (t >= view.start) minor.push(t);
    return { major: out, minor };
  }, [view.start, view.end, span]);

  // ─── Overview (pan) bar ───
  const overviewRef = useRef<HTMLDivElement>(null);
  const panDragRef = useRef<{ x: number; start: number } | null>(null);
  const onOverviewDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = overviewRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    const r = el.getBoundingClientRect();
    const at = rangeStartMs + clamp((e.clientX - r.left) / r.width, 0, 1) * fullSpan;
    // Outside the window: jump the window there first, then drag it.
    let start = view.start;
    if (at < view.start || at > view.end) { start = at - span / 2; setViewClamped(start, span); }
    panDragRef.current = { x: e.clientX, start };
  };
  const onOverviewMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = panDragRef.current;
    const el = overviewRef.current;
    if (!d || !el) return;
    const dx = (e.clientX - d.x) / el.getBoundingClientRect().width;
    setViewClamped(d.start + dx * fullSpan, span);
  };
  const onOverviewUp = () => { panDragRef.current = null; };

  const playheadIn = timeMs >= view.start && timeMs <= view.end;
  const lanesH = Math.max(1, lanes.length) * LANE_H;

  return (
    <div className={`flex flex-col overflow-hidden rounded-lg border border-border bg-card select-none ${className}`}>
      <div className="flex min-h-0">
        {/* ─── Left column: controls, then one label per lane ─── */}
        <div className={`${LABEL_W} flex-none border-r border-border`}>
          <div style={{ height: HEAD_H }} className="border-b border-border">{header}</div>
          {lanes.length === 0 ? (
            <div style={{ height: LANE_H }} className="flex items-center px-3 text-[11px] text-muted-foreground">No cameras on the wall</div>
          ) : lanes.map((l) => (
            <div key={l.key} style={{ height: LANE_H }} className="flex items-center gap-2 border-b border-border px-3 last:border-b-0">
              <span className="h-2 w-2 flex-none rounded-full" style={{ background: l.color }} />
              <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground" title={l.label}>{l.label}</span>
              {l.status && STATUS_TEXT[l.status] && (
                <span className={`flex-none text-[10px] ${l.status === 'error' ? 'text-red-400' : 'text-muted-foreground'}`}>{STATUS_TEXT[l.status]}</span>
              )}
            </div>
          ))}
        </div>

        {/* ─── Right column: axis + lanes (one scrub surface), playhead across both ─── */}
        <div className="relative min-w-0 flex-1">
          <div
            ref={trackRef}
            className="relative cursor-crosshair"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={() => setHover(null)}
          >
            {/* Axis */}
            <div style={{ height: HEAD_H }} className="relative border-b border-border">
              <div className="flex items-center justify-between gap-2 px-3 pt-2">
                <div className="min-w-0 truncate text-[12px] font-semibold uppercase tracking-wide text-foreground">
                  {dateLabel}
                  <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">showing {fmtSpan(span)}</span>
                </div>
                <div className="flex flex-none items-center gap-0.5" onPointerDown={(e) => e.stopPropagation()}>
                  <IconBtn label="Zoom out" onClick={() => zoomBy(1.6)} disabled={span >= fullSpan}><MagnifyingGlassMinusIcon className="h-4 w-4" /></IconBtn>
                  <IconBtn label="Zoom in" onClick={() => zoomBy(0.625)} disabled={span <= MIN_SPAN_MS}><MagnifyingGlassPlusIcon className="h-4 w-4" /></IconBtn>
                  <IconBtn label="Show whole range" onClick={() => setViewClamped(rangeStartMs, fullSpan)} disabled={span >= fullSpan}><ArrowsPointingOutIcon className="h-4 w-4" /></IconBtn>
                </div>
              </div>
              {ticks.minor.map((t) => (
                <div key={`m${t}`} className="absolute bottom-0 h-1.5 w-px bg-border" style={{ left: `${pct(t)}%` }} />
              ))}
              {ticks.major.map((t) => {
                // Labels are centred on their tick, except at the edges, where centring would
                // push half the label outside the axis.
                const p = pct(t.ms);
                const align = p < 3 ? 'items-start' : p > 97 ? '-translate-x-full items-end' : '-translate-x-1/2 items-center';
                return (
                  <div key={t.ms} className={`absolute bottom-0 flex flex-col ${align}`} style={{ left: `${p}%` }}>
                    <span className="mb-1 font-mono text-[11px] text-muted-foreground tabular-nums">{t.label}</span>
                    <span className="h-3 w-px bg-muted-foreground" />
                  </div>
                );
              })}
            </div>

            {/* Lanes */}
            <div style={{ height: lanesH }} className="relative">
              {ticks.major.map((t) => (
                <div key={`g${t.ms}`} className="pointer-events-none absolute inset-y-0 w-px bg-border" style={{ left: `${pct(t.ms)}%` }} />
              ))}
              {lanes.map((l, i) => (
                <div key={l.key} className="absolute inset-x-0" style={{ top: i * LANE_H, height: LANE_H }}>
                  <div className="absolute inset-x-0 top-1 bottom-1 rounded-sm bg-muted" />
                  {(l.segments ? blocksFor(l.segments, view.start, view.end) : []).map((b) => {
                    const left = Math.max(0, pct(b.start));
                    const right = Math.min(100, pct(b.end));
                    return (
                      <div
                        key={`${b.start}-${b.trigger}`}
                        className="absolute top-1 bottom-1 rounded-sm"
                        style={{
                          left: `${left}%`,
                          width: `max(2px, ${right - left}%)`,
                          background: TRIGGER_COLOURS[b.trigger] || TRIGGER_COLOURS.continuous,
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Hover line + time */}
            {hover != null && !draggingRef.current && (
              <div className="pointer-events-none absolute inset-y-0 z-10 w-px bg-foreground opacity-40" style={{ left: `${pct(hover)}%` }}>
                <span className="absolute left-1 top-10 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white">
                  {fmtTick(hover, true)}
                </span>
              </div>
            )}

            {/* Playhead — through the axis and every lane */}
            {playheadIn && (
              <div className="pointer-events-none absolute inset-y-0 z-20" style={{ left: `${pct(timeMs)}%` }}>
                <div className="absolute inset-y-0 -ml-px w-0.5 bg-red-500" />
                <div className="absolute -ml-1.5 h-0 w-0 border-x-[6px] border-t-[8px] border-x-transparent border-t-red-500" style={{ top: HEAD_H - 8 }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Footer: legend | overview bar ─── */}
      <div className="flex border-t border-border">
        <div className={`${LABEL_W} flex flex-none items-center gap-2.5 overflow-hidden border-r border-border px-3 py-1.5`}>
          {(['continuous', 'event', 'motion', 'manual'] as const).map((k) => (
            <span key={k} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="h-2 w-2 rounded-sm" style={{ background: TRIGGER_COLOURS[k] }} />
              {TRIGGER_LABELS[k]}
            </span>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 items-center px-3 py-1.5">
          <div
            ref={overviewRef}
            onPointerDown={onOverviewDown}
            onPointerMove={onOverviewMove}
            onPointerUp={onOverviewUp}
            onPointerCancel={onOverviewUp}
            className="relative h-2.5 w-full cursor-grab rounded-full bg-muted active:cursor-grabbing"
            title="Drag to move along the range"
          >
            <div
              className="absolute inset-y-0 rounded-full bg-brand"
              style={{
                left: `${((view.start - rangeStartMs) / fullSpan) * 100}%`,
                width: `max(8px, ${(span / fullSpan) * 100}%)`,
              }}
            />
            <div
              className="absolute -top-0.5 h-3.5 w-0.5 bg-red-500"
              style={{ left: `${clamp(((timeMs - rangeStartMs) / fullSpan) * 100, 0, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
